import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GameController, Robot, Users, Trophy, Copy, ArrowsClockwise, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, CornersOut, CornersIn, MagnifyingGlass, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace(/^http/, "ws");

// Game tunables
const FIELD = 480;            // logical playfield (square, top-down padel court)
const PADDLE_LEN = 90;
const PADDLE_THICK = 12;
const BALL_SIZE = 14;
const PADDLE_INSET = 22;       // distance from edge
const BASE_SPEED = 4.2;
const SPEED_INC = 1.04;        // each paddle hit
const MAX_SPEED = 11;
const TICK_MS = 1000 / 60;
const NET_MS = 1000 / 30;      // network broadcast cadence
const MAX_LIVES = 5;

const SLOTS = [
  { id: 0, name: "Norte",  side: "top",    color: "#CCFF00", text: "#000", keys: { left: "a", right: "d" } },
  { id: 1, name: "Sur",    side: "bottom", color: "#FF3B30", text: "#FFF", keys: { left: "ArrowLeft", right: "ArrowRight" } },
  { id: 2, name: "Oeste",  side: "left",   color: "#00D4FF", text: "#000", keys: { up: "w", down: "s" } },
  { id: 3, name: "Este",   side: "right",  color: "#FF6B00", text: "#FFF", keys: { up: "i", down: "k" } },
];

function makeBall() {
  const ang = (Math.random() * Math.PI / 2) + Math.PI / 4; // avoid pure axis
  const dir = Math.random() < 0.5 ? 1 : -1;
  return {
    x: FIELD / 2,
    y: FIELD / 2,
    vx: BASE_SPEED * Math.cos(ang) * dir,
    vy: BASE_SPEED * Math.sin(ang) * (Math.random() < 0.5 ? 1 : -1),
  };
}

function initState() {
  return {
    ball: makeBall(),
    paddles: { 0: FIELD / 2, 1: FIELD / 2, 2: FIELD / 2, 3: FIELD / 2 },
    lives: { 0: MAX_LIVES, 1: MAX_LIVES, 2: MAX_LIVES, 3: MAX_LIVES },
    eliminated: { 0: false, 1: false, 2: false, 3: false },
    winner: null,
    paused: true,
  };
}

export default function AtariTennis() {
  const [mode, setMode] = useState(null); // 'solo' | 'host' | 'guest'
  const [roomCode, setRoomCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [mySlot, setMySlot] = useState(1); // default human is south
  const [roster, setRoster] = useState([]); // [{slot, user_id, name}]
  const [hostId, setHostId] = useState(null);
  const myUserIdRef = useRef(null);
  const stateRef = useRef(initState());
  const wsRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef({}); // keyboard
  const aiRef = useRef({});
  const remotePaddleRef = useRef({});
  const lastBroadcastRef = useRef(0);
  const arrowHoldRef = useRef({}); // on-screen arrow press state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [inviteSearch, setInviteSearch] = useState("");
  // Difficulty per slot: { 0: 'medium', 2: 'medium', 3: 'medium' } — only for bot slots
  const [botDifficulty, setBotDifficulty] = useState({ 0: "medium", 2: "medium", 3: "medium" });
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  // ==== Keyboard input ====
  useEffect(() => {
    const dn = (e) => { inputRef.current[e.key] = true; };
    const up = (e) => { inputRef.current[e.key] = false; };
    window.addEventListener("keydown", dn);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
  }, []);

  // ==== Touch input (drag) for current human paddle ====
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const move = (e) => {
      const rect = cv.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      const sx = (t.clientX - rect.left) * (FIELD / rect.width);
      const sy = (t.clientY - rect.top) * (FIELD / rect.height);
      const slot = SLOTS[mySlot];
      const s = stateRef.current;
      if (slot.side === "top" || slot.side === "bottom") s.paddles[mySlot] = clamp(sx, PADDLE_LEN/2, FIELD - PADDLE_LEN/2);
      else s.paddles[mySlot] = clamp(sy, PADDLE_LEN/2, FIELD - PADDLE_LEN/2);
    };
    cv.addEventListener("touchmove", (e) => { e.preventDefault(); move(e); }, { passive: false });
    cv.addEventListener("mousemove", (e) => { if (e.buttons === 1) move(e); });
    return () => {};
  }, [mySlot]);

  // ==== Render loop ====
  useEffect(() => {
    if (!mode) return;
    let raf;
    const tick = (ts) => {
      const s = stateRef.current;
      if (!s.paused && !s.winner) {
        applyHumanInput(s, mySlot);
        if (mode === "solo" || mode === "host") {
          // Authoritative simulation
          for (let slot = 0; slot < 4; slot++) {
            if (s.eliminated[slot]) continue;
            const isLocal = slot === mySlot;
            const isRemoteHuman = roster.some((r) => r.slot === slot && r.user_id !== myUserIdRef.current);
            if (!isLocal && !isRemoteHuman) {
              applyAI(s, slot, botDifficulty[slot] || "medium");
            } else if (isRemoteHuman) {
              // Apply remote paddle target
              const target = remotePaddleRef.current[slot];
              if (typeof target === "number") s.paddles[slot] = clamp(target, PADDLE_LEN/2, FIELD - PADDLE_LEN/2);
            }
          }
          stepPhysics(s);
          // Broadcast state @30Hz
          if (mode === "host" && wsRef.current?.readyState === 1 && ts - lastBroadcastRef.current > NET_MS) {
            wsRef.current.send(JSON.stringify({ type: "state", data: { ball: s.ball, paddles: s.paddles, lives: s.lives, eliminated: s.eliminated, winner: s.winner } }));
            lastBroadcastRef.current = ts;
          }
        } else if (mode === "guest") {
          // Just send my paddle position
          if (wsRef.current?.readyState === 1 && ts - lastBroadcastRef.current > NET_MS) {
            wsRef.current.send(JSON.stringify({ type: "paddle", data: { slot: mySlot, value: s.paddles[mySlot] } }));
            lastBroadcastRef.current = ts;
          }
        }
      }
      drawCourt(canvasRef.current, s, mySlot, roster);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, mySlot, roster, botDifficulty]);

  // ==== Solo Mode ====
  const startSolo = () => {
    stateRef.current = initState();
    stateRef.current.paused = false;
    setMySlot(1);
    setRoster([{ slot: 0, name: "Bot Norte", user_id: "bot_0" }, { slot: 1, name: "Tú", user_id: "me" }, { slot: 2, name: "Bot Oeste", user_id: "bot_2" }, { slot: 3, name: "Bot Este", user_id: "bot_3" }]);
    setMode("solo");
  };

  // ==== Online Mode ====
  const createRoom = async () => {
    try {
      const { data } = await api.post("/atari/rooms", {});
      setRoomCode(data.room_code);
      connectWs(data.room_code, true);
    } catch (e) { toast.error("Error al crear sala"); }
  };

  const joinRoom = () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) { toast.error("Código requerido"); return; }
    setRoomCode(code);
    connectWs(code, false);
  };

  const connectWs = (code, asHost) => {
    const token = localStorage.getItem("padel_token");
    if (!token) { toast.error("Sesión expirada"); return; }
    const wsProto = API_URL.startsWith("https") ? "wss" : "ws";
    const host = API_URL.replace(/^https?:\/\//, "");
    const url = `${wsProto}://${host}/api/ws/atari/${code}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    let opened = false;
    ws.onopen = () => {
      opened = true;
      stateRef.current = initState();
      stateRef.current.paused = false;
      setMode(asHost ? "host" : "guest");
      toast.success(asHost ? `Sala creada: ${code}` : "Conectado a sala");
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "welcome") {
          myUserIdRef.current = msg.data.user_id;
          setMySlot(msg.data.slot);
        } else if (msg.type === "roster") {
          setRoster(msg.data.members || []);
          setHostId(msg.data.host_id);
        } else if (msg.type === "state") {
          // Guests apply authoritative state from host
          const s = stateRef.current;
          Object.assign(s, msg.data);
        } else if (msg.type === "paddle") {
          // Host receives a guest's paddle target
          remotePaddleRef.current[msg.data.slot] = msg.data.value;
        } else if (msg.type === "error") {
          toast.error(msg.data?.message || "Error");
        }
      } catch {}
    };
    ws.onclose = (ev) => {
      wsRef.current = null;
      if (opened && ev.code !== 1000 && ev.code !== 1001) {
        toast.warning("Sala cerrada");
      }
    };
    ws.onerror = () => {
      if (!opened) toast.error("No se pudo conectar a la sala");
    };
  };

  const leaveGame = () => {
    if (wsRef.current) wsRef.current.close();
    wsRef.current = null;
    setMode(null);
    setRoster([]);
    setRoomCode("");
    setJoinCode("");
  };

  // Auto-join from URL param ?join=CODE (came from invite notification)
  useEffect(() => {
    if (mode) return;
    const params = new URLSearchParams(window.location.search);
    const code = (params.get("join") || "").toUpperCase().trim();
    if (code && /^[A-Z0-9]{4,8}$/.test(code)) {
      setRoomCode(code);
      connectWs(code, false);
      // Clean URL
      window.history.replaceState({}, "", "/dashboard/atari");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==== Responsive canvas size: auto-adjust to viewport ====
  const [canvasSize, setCanvasSize] = useState(480);
  const [orientation, setOrientation] = useState("portrait");
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isLandscape = w > h;
      setOrientation(isLandscape ? "landscape" : "portrait");
      const sidebarW = w >= 1024 ? 256 : 0;
      const cardPad = 24;       // card-tactical padding
      const arrowSpace = 90;    // arrows row/column footprint
      const headerSpace = 220;  // header + scoreboard rows
      const safeMargin = 40;
      const availW = w - sidebarW - cardPad * 2 - safeMargin;
      const availH = h - headerSpace - cardPad * 2;
      let target;
      if (isFullscreen) {
        target = Math.min(w, h) * 0.92;
      } else if (isLandscape) {
        // side-by-side: canvas + arrows column
        target = Math.min(availW - arrowSpace, availH);
      } else {
        // stacked: canvas above arrows row
        target = Math.min(availW, availH - arrowSpace);
      }
      target = Math.max(260, Math.min(720, target));
      setCanvasSize(Math.floor(target));
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, [isFullscreen]);

  const restartGame = () => {
    if (mode === "solo" || mode === "host") {
      stateRef.current = initState();
      stateRef.current.paused = false;
    }
  };

  // ==== Fullscreen toggle ====
  const toggleFullscreen = async () => {
    try {
      const el = wrapperRef.current;
      if (!el) return;
      if (!document.fullscreenElement) {
        await (el.requestFullscreen?.() || el.webkitRequestFullscreen?.());
        setIsFullscreen(true);
      } else {
        await (document.exitFullscreen?.() || document.webkitExitFullscreen?.());
        setIsFullscreen(false);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ==== Arrow press helpers (touch + click) ====
  const pressArrow = (dir, on) => {
    arrowHoldRef.current[dir] = on;
    // Also write into global keys so applyHumanInput reads them
    const k = SLOTS[mySlot].keys;
    const map = {
      "left":  k.left  || "ArrowLeft",
      "right": k.right || "ArrowRight",
      "up":    k.up    || "ArrowUp",
      "down":  k.down  || "ArrowDown",
    };
    if (typeof window !== "undefined") {
      window.__atariKeys = window.__atariKeys || {};
      window.__atariKeys[map[dir]] = on;
    }
  };

  // ==== Invite users to room ====
  const loadInviteUsers = async () => {
    try {
      const { data } = await api.get("/users");
      // Exclude self + non-active
      setAllUsers((data || []).filter((u) => u.id !== myUserIdRef.current && u.is_active !== false));
    } catch { toast.error("Error al cargar usuarios"); }
  };
  const openInvite = () => {
    setInviteOpen(true);
    if (allUsers.length === 0) loadInviteUsers();
  };
  const sendInvite = async (targetUserId, targetName) => {
    try {
      await api.post(`/atari/rooms/${roomCode}/invite`, { user_id: targetUserId });
      toast.success(`Invitación enviada a ${targetName}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al invitar");
    }
  };
  const filteredInviteUsers = useMemo(() => {
    const q = inviteSearch.trim().toLowerCase();
    const inRoom = new Set(roster.map((r) => r.user_id));
    return allUsers
      .filter((u) => !inRoom.has(u.id))
      .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [allUsers, inviteSearch, roster]);

  if (!mode) {
    return (
      <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="atari-tennis-lobby">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Gamificación</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Atari Tenis · 4 Jugadores</h1>
          <p className="text-sm text-gray-400 mt-1">Pong retro sobre cancha de pádel top-panorámica. Solo vs bots o online con WebSockets.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card-tactical p-6 hover:border-[#CCFF00]/40 transition-colors" data-testid="atari-mode-solo">
            <div className="flex items-center gap-3 mb-3">
              <Robot size={28} weight="duotone" className="text-[#CCFF00]" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Modo</p>
                <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Solo · 1v3 Bots</h2>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3">Tú al sur. 3 bots IA en las otras 3 paredes. Sobrevive el último golpeando la bola.</p>
            <div className="mb-3" data-testid="atari-global-difficulty">
              <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400 mb-1.5">Dificultad de los 3 bots</p>
              <div className="grid grid-cols-4 gap-1">
                {["easy", "medium", "hard", "expert"].map((lvl) => {
                  const labels = { easy: "Fácil", medium: "Medio", hard: "Difícil", expert: "Experto" };
                  const active = (botDifficulty[0] || "medium") === lvl;
                  return (
                    <button
                      key={lvl}
                      onClick={(e) => { e.stopPropagation(); setBotDifficulty({ 0: lvl, 2: lvl, 3: lvl }); }}
                      className={`px-2 py-1.5 rounded text-[10px] uppercase font-bold border transition-all ${active ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-[0_0_12px_rgba(204,255,0,0.4)]" : "bg-white/5 text-white border-white/15 hover:bg-white/10"}`}
                      data-testid={`atari-global-diff-${lvl}`}
                    >
                      {labels[lvl]}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={startSolo} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] uppercase font-bold text-xs w-full" data-testid="atari-start-solo">
              <GameController size={16} className="mr-2" /> Jugar ahora
            </Button>
          </div>

          <div className="card-tactical p-6" data-testid="atari-mode-online">
            <div className="flex items-center gap-3 mb-3">
              <Users size={28} weight="duotone" className="text-[#FF6B00]" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF6B00]">Modo</p>
                <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Online · 4P</h2>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3">Crea una sala o entra con un código. Hasta 4 jugadores en dispositivos distintos.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={createRoom} className="bg-[#FF6B00] text-white hover:bg-[#FF8533] uppercase font-bold text-xs" data-testid="atari-create-room">
                Crear sala
              </Button>
              <div className="flex gap-2">
                <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="CÓDIGO" maxLength={5}
                  className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#FF6B00] uppercase font-['JetBrains_Mono'] text-center tracking-[0.3em]"
                  data-testid="atari-join-code" />
                <Button onClick={joinRoom} variant="outline" className="border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/10 uppercase text-xs font-bold" data-testid="atari-join-room">
                  Entrar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="card-tactical p-4 text-[11px] text-gray-400 leading-relaxed">
          <p className="text-[#CCFF00] uppercase tracking-widest text-[10px] font-bold mb-2">Controles</p>
          <p>· <span className="text-white">Norte (P1):</span> A / D · <span className="text-white">Sur (P2):</span> ← / → · <span className="text-white">Oeste (P3):</span> W / S · <span className="text-white">Este (P4):</span> I / K</p>
          <p className="mt-1">En móvil: arrastra el dedo sobre la cancha para mover tu pala.</p>
        </div>
      </div>
    );
  }

  // ==== Responsive canvas size: auto-adjust to viewport ====
  const s = stateRef.current;
  return (
    <div className="space-y-4 animate-fade-in" data-testid="atari-tennis-game">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Atari Tenis</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase">
            {mode === "solo" ? "Solo · 1v3 Bots" : `Online · ${mode === "host" ? "Host" : "Guest"}`}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {roomCode && (
            <Badge variant="outline" className="bg-[#FF6B00]/10 text-[#FF6B00] border-[#FF6B00]/30 font-['JetBrains_Mono'] tracking-[0.3em] cursor-pointer"
              onClick={() => { navigator.clipboard.writeText(roomCode); toast.success("Código copiado"); }}
              data-testid="atari-room-code">
              <Copy size={12} className="mr-1" /> {roomCode}
            </Badge>
          )}
          {(mode === "solo" || mode === "host") && (
            <Button onClick={restartGame} variant="outline" size="sm" className="border-white/20 text-gray-300 uppercase text-[10px]" data-testid="atari-restart">
              <ArrowsClockwise size={12} className="mr-1" /> Reiniciar
            </Button>
          )}
          {mode === "host" && roomCode && (
            <Button onClick={openInvite} variant="outline" size="sm" className="border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/10 uppercase text-[10px]" data-testid="atari-invite-btn">
              <PaperPlaneTilt size={12} className="mr-1" /> Invitar
            </Button>
          )}
          <Button onClick={toggleFullscreen} variant="outline" size="sm" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-[10px]" data-testid="atari-fullscreen">
            {isFullscreen ? <CornersIn size={12} className="mr-1" /> : <CornersOut size={12} className="mr-1" />}
            {isFullscreen ? "Salir" : "Pantalla completa"}
          </Button>
          <Button onClick={leaveGame} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 uppercase text-[10px]" data-testid="atari-leave">
            Salir partida
          </Button>
        </div>
      </div>

      {/* Score panel */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="atari-scoreboard">
        {SLOTS.map((sl) => {
          const member = roster.find((r) => r.slot === sl.id);
          const lives = s.lives[sl.id];
          const isMe = sl.id === mySlot;
          const elim = s.eliminated[sl.id];
          // A bot slot is one where member.user_id starts with 'bot_' OR no real member (solo + non-mySlot)
          const isBot = !!member && typeof member.user_id === "string" && member.user_id.startsWith("bot_");
          const currentDiff = botDifficulty[sl.id] || "medium";
          return (
            <div key={sl.id} className={`border p-3 ${elim ? "opacity-40 grayscale" : ""} ${isMe ? "ring-2 ring-[#CCFF00]" : "border-white/10"}`}
              style={{ backgroundColor: `${sl.color}15` }} data-testid={`atari-score-${sl.id}`}>
              <p className="text-[9px] uppercase tracking-[0.2em] text-gray-400">{sl.name} {isMe && "· Tú"} {isBot && "· BOT"}</p>
              <p className="text-sm font-bold truncate" style={{ color: sl.color }}>{member?.name || `Bot ${sl.name}`}</p>
              <div className="flex gap-0.5 mt-1.5" aria-label={`vidas: ${lives}`}>
                {Array.from({ length: MAX_LIVES }).map((_, i) => (
                  <span key={i} className="w-2 h-2" style={{ backgroundColor: i < lives ? sl.color : "#1A1A1A" }} />
                ))}
              </div>
              {isBot && (
                <div className="mt-2 flex flex-wrap gap-0.5" data-testid={`atari-bot-difficulty-${sl.id}`}>
                  {["easy", "medium", "hard", "expert"].map((lvl) => {
                    const active = currentDiff === lvl;
                    const labels = { easy: "Fácil", medium: "Medio", hard: "Difícil", expert: "Experto" };
                    return (
                      <button
                        key={lvl}
                        onClick={() => setBotDifficulty((b) => ({ ...b, [sl.id]: lvl }))}
                        className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold border transition-all flex-1 min-w-[40px] ${active ? "text-black border-current" : "bg-black/30 text-gray-400 border-white/10 hover:text-white"}`}
                        style={active ? { backgroundColor: sl.color, borderColor: sl.color } : {}}
                        data-testid={`atari-bot-diff-${sl.id}-${lvl}`}
                      >
                        {labels[lvl]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Canvas + arrows: responsive layout */}
      <div
        ref={wrapperRef}
        className={`card-tactical p-3 sm:p-6 flex ${orientation === "landscape" ? "flex-row" : "flex-col"} items-center justify-center gap-3 sm:gap-6 ${isFullscreen ? "bg-[#0A0A0A] !p-2 !flex-col" : ""}`}
        data-testid="atari-canvas-wrapper"
      >
        <canvas
          ref={canvasRef}
          width={FIELD}
          height={FIELD}
          style={{
            width: `${canvasSize}px`,
            height: `${canvasSize}px`,
            maxWidth: "100%",
            maxHeight: "100%",
            imageRendering: "pixelated",
            touchAction: "none",
            display: "block",
          }}
          className="border-2 border-[#CCFF00]/30 bg-[#0A2A1F] shrink-0"
          data-testid="atari-canvas"
        />

        {/* On-screen arrow controls (visible always; sized larger on mobile) */}
        <ArrowControls slot={SLOTS[mySlot]} onPress={pressArrow} />
      </div>

      {/* Invite dialog (host) */}
      {inviteOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setInviteOpen(false)} data-testid="atari-invite-dialog">
          <div className="bg-[#141414] border border-white/10 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF6B00] mb-1">Sala {roomCode}</p>
              <h3 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Invitar Jugadores</h3>
              <p className="text-[11px] text-gray-500 mt-1">Selecciona usuarios registrados para enviar invitación con el código de sala.</p>
            </div>
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input value={inviteSearch} onChange={(e) => setInviteSearch(e.target.value)}
                  placeholder="Buscar por nombre o email..."
                  className="bg-[#0A0A0A] border-white/20 text-white pl-9 h-10 focus:border-[#FF6B00]"
                  data-testid="atari-invite-search" />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredInviteUsers.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-500">{inviteSearch ? "Sin resultados" : "Cargando..."}</p>
              ) : filteredInviteUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-3 border-b border-white/5 hover:bg-white/5" data-testid={`atari-invite-user-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-semibold truncate">{u.name}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">{u.role} · {u.email}</p>
                  </div>
                  <Button size="sm" onClick={() => sendInvite(u.id, u.name)} className="bg-[#FF6B00] text-white hover:bg-[#FF8533] uppercase text-[10px] font-bold h-8" data-testid={`atari-invite-send-${u.id}`}>
                    <PaperPlaneTilt size={12} className="mr-1" /> Invitar
                  </Button>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/10 flex justify-end">
              <Button onClick={() => setInviteOpen(false)} variant="outline" className="border-white/20 text-gray-300 uppercase text-xs">Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {s.winner !== null && (
        <div className="card-tactical p-6 text-center border-[#CCFF00]/40" data-testid="atari-winner-banner">
          <Trophy size={32} weight="fill" className="text-[#CCFF00] mx-auto mb-2" />
          <p className="font-['Barlow_Condensed'] text-3xl font-black uppercase" style={{ color: SLOTS[s.winner].color }}>
            ¡Gana {roster.find((r) => r.slot === s.winner)?.name || SLOTS[s.winner].name}!
          </p>
          <Button onClick={restartGame} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] uppercase font-bold text-xs mt-4">
            Revancha
          </Button>
        </div>
      )}
    </div>
  );
}

// ===========================================================
// On-screen arrow controls — always visible. Buttons act on
// press/release so users can hold to keep moving the paddle.
// ===========================================================
function ArrowControls({ slot, onPress }) {
  const horizontal = slot.side === "top" || slot.side === "bottom";
  const press = (dir, on) => (e) => { e.preventDefault(); onPress(dir, on); };
  const btnCls = "flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 border-2 select-none active:bg-[#CCFF00] active:text-black active:scale-95 transition-all uppercase font-black text-sm";
  const palette = {
    base: { borderColor: slot.color + "60", color: slot.color, backgroundColor: slot.color + "10" },
  };
  if (horizontal) {
    return (
      <div className="flex items-center gap-3" data-testid="atari-arrows-horizontal">
        <button type="button" className={btnCls} style={palette.base}
          onMouseDown={press("left", true)} onMouseUp={press("left", false)} onMouseLeave={press("left", false)}
          onTouchStart={press("left", true)} onTouchEnd={press("left", false)} onTouchCancel={press("left", false)}
          data-testid="atari-arrow-left" aria-label="Mover izquierda">
          <ArrowLeft size={28} weight="bold" />
        </button>
        <button type="button" className={btnCls} style={palette.base}
          onMouseDown={press("right", true)} onMouseUp={press("right", false)} onMouseLeave={press("right", false)}
          onTouchStart={press("right", true)} onTouchEnd={press("right", false)} onTouchCancel={press("right", false)}
          data-testid="atari-arrow-right" aria-label="Mover derecha">
          <ArrowRight size={28} weight="bold" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3" data-testid="atari-arrows-vertical">
      <button type="button" className={btnCls} style={palette.base}
        onMouseDown={press("up", true)} onMouseUp={press("up", false)} onMouseLeave={press("up", false)}
        onTouchStart={press("up", true)} onTouchEnd={press("up", false)} onTouchCancel={press("up", false)}
        data-testid="atari-arrow-up" aria-label="Mover arriba">
        <ArrowUp size={28} weight="bold" />
      </button>
      <button type="button" className={btnCls} style={palette.base}
        onMouseDown={press("down", true)} onMouseUp={press("down", false)} onMouseLeave={press("down", false)}
        onTouchStart={press("down", true)} onTouchEnd={press("down", false)} onTouchCancel={press("down", false)}
        data-testid="atari-arrow-down" aria-label="Mover abajo">
        <ArrowDown size={28} weight="bold" />
      </button>
    </div>
  );
}

// ===========================================================
// Game logic helpers (pure - operate on state ref)
// ===========================================================
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

function applyHumanInput(s, slot) {
  const sl = SLOTS[slot];
  if (s.eliminated[slot]) return;
  const speed = 6;
  const inp = window.__atariInputs || {};
  // Use captured global keys
  const keys = window.__atariKeys || {};
  const k = keysFor(sl);
  let pos = s.paddles[slot];
  if (sl.side === "top" || sl.side === "bottom") {
    if (keys[k.left]) pos -= speed;
    if (keys[k.right]) pos += speed;
  } else {
    if (keys[k.up]) pos -= speed;
    if (keys[k.down]) pos += speed;
  }
  s.paddles[slot] = clamp(pos, PADDLE_LEN / 2, FIELD - PADDLE_LEN / 2);
}

function keysFor(slot) { return slot.keys; }

function applyAI(s, slot, difficulty = "medium") {
  const sl = SLOTS[slot];
  // Tunables by difficulty
  // - lag: lower = faster reaction (snappier paddle)
  // - prediction: look-ahead ticks (anticipates ball trajectory)
  // - errorPx: jitter at target (humanize mistakes)
  // - speedCap: cap on max paddle delta per frame
  const tuning = {
    easy:   { lag: 0.06, prediction: 0,  errorPx: 35, speedCap: 4.5 },
    medium: { lag: 0.14, prediction: 8,  errorPx: 14, speedCap: 7.5 },
    hard:   { lag: 0.22, prediction: 18, errorPx: 5,  speedCap: 11  },
    expert: { lag: 0.34, prediction: 28, errorPx: 0,  speedCap: 16  },
  }[difficulty] || { lag: 0.14, prediction: 8, errorPx: 14, speedCap: 7.5 };

  // Predict ball position N ticks ahead (simple linear extrapolation, no wall bounce model)
  const future = {
    x: s.ball.x + s.ball.vx * tuning.prediction,
    y: s.ball.y + s.ball.vy * tuning.prediction,
  };
  // Wrap reflection for prediction over walls (very simple)
  const wrap = (v) => {
    let p = v;
    while (p < 0) p = -p;
    while (p > FIELD) p = 2 * FIELD - p;
    return p;
  };
  const futureX = wrap(future.x);
  const futureY = wrap(future.y);
  let target = (sl.side === "top" || sl.side === "bottom") ? futureX : futureY;
  // Add error
  if (tuning.errorPx > 0) target += (Math.random() - 0.5) * 2 * tuning.errorPx;
  const cur = s.paddles[slot];
  const delta = (target - cur) * tuning.lag;
  const capped = Math.max(-tuning.speedCap, Math.min(tuning.speedCap, delta));
  s.paddles[slot] = clamp(cur + capped, PADDLE_LEN / 2, FIELD - PADDLE_LEN / 2);
}

function stepPhysics(s) {
  if (s.winner !== null) return;
  s.ball.x += s.ball.vx;
  s.ball.y += s.ball.vy;

  // Collision with paddles or scoring on each side
  // top (y near PADDLE_INSET): paddle horizontal at y=PADDLE_INSET, x range [paddles[0]-len/2, +len/2]
  const margin = PADDLE_INSET + PADDLE_THICK / 2;
  // TOP - slot 0
  if (s.ball.y - BALL_SIZE / 2 <= margin && s.ball.vy < 0) {
    if (!s.eliminated[0] && Math.abs(s.ball.x - s.paddles[0]) < PADDLE_LEN / 2 + BALL_SIZE / 2) {
      s.ball.vy = Math.abs(s.ball.vy);
      s.ball.vx += (s.ball.x - s.paddles[0]) * 0.12;
      bumpSpeed(s);
    } else if (s.ball.y < 0) {
      loseLife(s, 0);
    }
  }
  // BOTTOM - slot 1
  if (s.ball.y + BALL_SIZE / 2 >= FIELD - margin && s.ball.vy > 0) {
    if (!s.eliminated[1] && Math.abs(s.ball.x - s.paddles[1]) < PADDLE_LEN / 2 + BALL_SIZE / 2) {
      s.ball.vy = -Math.abs(s.ball.vy);
      s.ball.vx += (s.ball.x - s.paddles[1]) * 0.12;
      bumpSpeed(s);
    } else if (s.ball.y > FIELD) {
      loseLife(s, 1);
    }
  }
  // LEFT - slot 2
  if (s.ball.x - BALL_SIZE / 2 <= margin && s.ball.vx < 0) {
    if (!s.eliminated[2] && Math.abs(s.ball.y - s.paddles[2]) < PADDLE_LEN / 2 + BALL_SIZE / 2) {
      s.ball.vx = Math.abs(s.ball.vx);
      s.ball.vy += (s.ball.y - s.paddles[2]) * 0.12;
      bumpSpeed(s);
    } else if (s.ball.x < 0) {
      loseLife(s, 2);
    }
  }
  // RIGHT - slot 3
  if (s.ball.x + BALL_SIZE / 2 >= FIELD - margin && s.ball.vx > 0) {
    if (!s.eliminated[3] && Math.abs(s.ball.y - s.paddles[3]) < PADDLE_LEN / 2 + BALL_SIZE / 2) {
      s.ball.vx = -Math.abs(s.ball.vx);
      s.ball.vy += (s.ball.y - s.paddles[3]) * 0.12;
      bumpSpeed(s);
    } else if (s.ball.x > FIELD) {
      loseLife(s, 3);
    }
  }
  // Closed wall for eliminated players (their wall acts as bounce)
  for (let i = 0; i < 4; i++) {
    if (!s.eliminated[i]) continue;
    const sd = SLOTS[i].side;
    if (sd === "top" && s.ball.y - BALL_SIZE / 2 <= 4 && s.ball.vy < 0) s.ball.vy = Math.abs(s.ball.vy);
    if (sd === "bottom" && s.ball.y + BALL_SIZE / 2 >= FIELD - 4 && s.ball.vy > 0) s.ball.vy = -Math.abs(s.ball.vy);
    if (sd === "left" && s.ball.x - BALL_SIZE / 2 <= 4 && s.ball.vx < 0) s.ball.vx = Math.abs(s.ball.vx);
    if (sd === "right" && s.ball.x + BALL_SIZE / 2 >= FIELD - 4 && s.ball.vx > 0) s.ball.vx = -Math.abs(s.ball.vx);
  }
}

function bumpSpeed(s) {
  const sp = Math.hypot(s.ball.vx, s.ball.vy);
  const ns = Math.min(MAX_SPEED, sp * SPEED_INC);
  const a = Math.atan2(s.ball.vy, s.ball.vx);
  s.ball.vx = Math.cos(a) * ns;
  s.ball.vy = Math.sin(a) * ns;
}

function loseLife(s, slot) {
  s.lives[slot] = Math.max(0, s.lives[slot] - 1);
  if (s.lives[slot] === 0) s.eliminated[slot] = true;
  // reset ball
  s.ball = makeBall();
  // Check winner
  const alive = [0,1,2,3].filter((i) => !s.eliminated[i]);
  if (alive.length === 1) s.winner = alive[0];
}

// ===========================================================
// Drawing - retro pixel-art on top-down padel court
// ===========================================================
function drawCourt(canvas, s, mySlot, roster) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = false;

  // Padel court floor (greenish-blue clay/turf)
  ctx.fillStyle = "#0A2A1F";
  ctx.fillRect(0, 0, FIELD, FIELD);

  // Diagonal pixel-art turf texture
  ctx.fillStyle = "rgba(255,255,255,0.025)";
  for (let i = 0; i < FIELD; i += 8) {
    for (let j = 0; j < FIELD; j += 8) {
      if (((i + j) / 8) % 2 === 0) ctx.fillRect(i, j, 8, 8);
    }
  }

  // Court markings (white pixel lines)
  ctx.fillStyle = "#FFFFFF";
  // Outer border
  drawPxRect(ctx, 4, 4, FIELD - 8, 2);
  drawPxRect(ctx, 4, FIELD - 6, FIELD - 8, 2);
  drawPxRect(ctx, 4, 4, 2, FIELD - 8);
  drawPxRect(ctx, FIELD - 6, 4, 2, FIELD - 8);
  // Net (horizontal middle line - pixel dashed)
  for (let x = 8; x < FIELD - 8; x += 12) drawPxRect(ctx, x, FIELD / 2 - 1, 8, 2);
  // Service boxes (vertical centerline within front area)
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  drawPxRect(ctx, FIELD / 2 - 1, FIELD / 4 + 12, 2, FIELD / 2 - 24);

  // Glass walls vibe (corners darker)
  const grad = ctx.createRadialGradient(FIELD/2, FIELD/2, FIELD/3, FIELD/2, FIELD/2, FIELD/1.2);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.5)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, FIELD, FIELD);

  // Paddles (pixel chunky)
  for (let i = 0; i < 4; i++) {
    const sl = SLOTS[i];
    const pos = s.paddles[i];
    ctx.fillStyle = s.eliminated[i] ? "#333" : sl.color;
    if (sl.side === "top") drawPxRect(ctx, pos - PADDLE_LEN/2, PADDLE_INSET, PADDLE_LEN, PADDLE_THICK);
    if (sl.side === "bottom") drawPxRect(ctx, pos - PADDLE_LEN/2, FIELD - PADDLE_INSET - PADDLE_THICK, PADDLE_LEN, PADDLE_THICK);
    if (sl.side === "left") drawPxRect(ctx, PADDLE_INSET, pos - PADDLE_LEN/2, PADDLE_THICK, PADDLE_LEN);
    if (sl.side === "right") drawPxRect(ctx, FIELD - PADDLE_INSET - PADDLE_THICK, pos - PADDLE_LEN/2, PADDLE_THICK, PADDLE_LEN);
    // "MY" indicator
    if (i === mySlot) {
      ctx.fillStyle = "#FFFFFF";
      const px = sl.side === "top" ? [pos - 2, PADDLE_INSET - 6] :
                 sl.side === "bottom" ? [pos - 2, FIELD - PADDLE_INSET + PADDLE_THICK + 2] :
                 sl.side === "left" ? [PADDLE_INSET - 6, pos - 2] :
                 [FIELD - PADDLE_INSET + PADDLE_THICK + 2, pos - 2];
      drawPxRect(ctx, px[0], px[1], 4, 4);
    }
  }

  // Ball (pixel block)
  ctx.fillStyle = "#FFFFFF";
  drawPxRect(ctx, s.ball.x - BALL_SIZE/2, s.ball.y - BALL_SIZE/2, BALL_SIZE, BALL_SIZE);
  // ball trail
  ctx.fillStyle = "rgba(204,255,0,0.5)";
  drawPxRect(ctx, s.ball.x - BALL_SIZE/2 - s.ball.vx, s.ball.y - BALL_SIZE/2 - s.ball.vy, BALL_SIZE, BALL_SIZE);

  // CRT scanlines
  ctx.fillStyle = "rgba(0,0,0,0.12)";
  for (let y = 0; y < FIELD; y += 3) ctx.fillRect(0, y, FIELD, 1);
}

function drawPxRect(ctx, x, y, w, h) {
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

// Capture keys globally so input loop is reactive
if (typeof window !== "undefined" && !window.__atariKeysAttached) {
  window.__atariKeys = {};
  window.addEventListener("keydown", (e) => { window.__atariKeys[e.key] = true; });
  window.addEventListener("keyup", (e) => { window.__atariKeys[e.key] = false; });
  window.__atariKeysAttached = true;
}
